'use client';

import { ChevronsUpDown, X } from 'lucide-react';
import classNames from 'classnames';
import { FC, MouseEventHandler, useCallback, useEffect, useState } from 'react';
import { Button, ButtonProps } from './button';
import { ComboboxOption, ComboboxOptions, findOptionByValue, getOptionLabel } from './combobox-shared';
import { Command, CommandEmpty, CommandInput, CommandList } from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '../../utils/cn';
type ComboboxProps = {
  options?: ComboboxOption[];
  placeholder: string;
  value?: string;
  size?: ButtonProps['size'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (value: string, data?: any) => void;
  className?: string;
  disabled?: boolean;
  suppressDelete?: boolean;
  testIdKey?: string;
  darkMode?: boolean;
  inDialog?: boolean;
  Header?: FC<{
    onClose: () => void;
  }>;
} & ({
  filter: true;
  filterPlaceholder: string;
  emptyMessage: string;
} | {
  filter?: false;
});
export const Combobox: FC<ComboboxProps> = props => {
  const {
    options,
    placeholder,
    onChange,
    className,
    size,
    value,
    filter,
    disabled,
    suppressDelete,
    testIdKey,
    darkMode,
    inDialog = false,
    Header
  } = props;
  const [open, setOpen] = useState(false);
  const activeOption = options && value ? findOptionByValue(options, value) : null;
  const handleDelete = useCallback<MouseEventHandler>(event => {
    event.stopPropagation();
    onChange?.('');
  }, [onChange]);
  useEffect(() => {
    if (options?.length && value && !activeOption) {
      onChange?.('');
    }
  }, [options, value, activeOption, onChange]);
  return <Popover open={open} onOpenChange={setOpen} modal={inDialog} data-sentry-element="Popover" data-sentry-component="Combobox" data-sentry-source-file="combobox.tsx">
      <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="combobox.tsx">
        <Button variant="outline" role="combobox" aria-expanded={open} size={size} className={cn(className, 'cursor-default py-2.5 sm:py-3 px-4 mb-2 justify-between rounded-lg border border-gray-200 font-normal sm:text-sm', activeOption && !darkMode ? 'text-zinc-800' : 'text-gray-500', darkMode ? 'bg-gray-900 hover:text-zinc-500' : 'bg-white')} disabled={disabled} data-testid={`${testIdKey}-trigger`} data-sentry-element="Button" data-sentry-source-file="combobox.tsx">
          <span className="w-full truncate text-left">
            {activeOption ? getOptionLabel(activeOption) : placeholder}
          </span>
          {value && !suppressDelete && <X size={16} className="ml-2 shrink-0 opacity-70 hover:opacity-100" onClick={handleDelete} />}
          <ChevronsUpDown size={16} className="ml-2 shrink-0 opacity-50" data-sentry-element="ChevronsUpDown" data-sentry-source-file="combobox.tsx" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={classNames(className, 'min-w-[var(--radix-popper-anchor-width)]')} data-sentry-element="PopoverContent" data-sentry-source-file="combobox.tsx">
        <Command data-sentry-element="Command" data-sentry-source-file="combobox.tsx">
          {Header && <div className="border-b py-1">{<Header onClose={() => setOpen(false)} />}</div>}
          {filter && <>
              <CommandInput placeholder={props.filterPlaceholder} />
              <CommandEmpty>{props.emptyMessage}</CommandEmpty>
            </>}
          <CommandList data-sentry-element="CommandList" data-sentry-source-file="combobox.tsx">
            <ComboboxOptions options={options ?? []} value={value} onChange={onChange} onClose={() => setOpen(false)} data-sentry-element="ComboboxOptions" data-sentry-source-file="combobox.tsx" />
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>;
};